import clsx from "clsx";
import { useState } from "react";
import { numericFormatter } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { keycloak } from "../../../lib/keycloak";
import { useFindAllPreferencesQuery, useUpdatePreferencesMutation } from "../../../lib/preferences/api";
import { targetListColumns } from "../../../lib/preferences/dto";
import { useAppSelector } from "../../../lib/redux/hooks";
import { CopyIcon } from "../../../ui/icons/copy";
import { EditIcon } from "../../../ui/icons/edit";
import { TrashIcon } from "../../../ui/icons/trash";
import { UsersIcon } from "../../../ui/icons/users";
import { XCircleIcon } from "../../../ui/icons/xCircle";
import Avatar from "../../../ui/molecules/avatar";
import Pills from "../../../ui/molecules/pills";
import { Dropdown } from "../../../ui/organisms/dropdown";
import { CustomTable } from "../../../ui/organisms/table";
import { CustomerAccessEnum, UserDTO } from "../../users/dto";
import { FindAllTargetListsFilters, FindAllTargetListsResponse, targertListsStatePillMap, TargetListDTO } from "../dto";
import { DeleteTargetList } from "./delete";

export interface Props {
    targetLists: FindAllTargetListsResponse
    filters: FindAllTargetListsFilters
    teamLeaders: { user: UserDTO, avatar: string }[]
    commercialsList: { user: UserDTO, avatar: string }[]
    setFilters: (filters: Partial<FindAllTargetListsFilters>) => void
    setTargetListToCopy: (value: string | undefined) => void
    setShowCopyModal: (visible: boolean) => void
    isFromProject?: boolean
}
export function TargetListTable(props: Props) {
    const { targetLists, filters, setFilters, setTargetListToCopy, setShowCopyModal, teamLeaders, commercialsList, isFromProject } = props
    const navigate = useNavigate()
    const userState = useAppSelector(state => state.users);

    const [idToDelete, setIdToDelete] = useState<string | undefined>();
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    const { data: preferencesResponse } = useFindAllPreferencesQuery(
        keycloak.subject!,
        { skip: !keycloak.subject }
    );
    const [updatePreferencesMutation] = useUpdatePreferencesMutation();

    const getColumnWidth = (column: string) => {
        return preferencesResponse?.data[0]?.targetListPreferences?.find(
            col => col.column === column
        )?.width ?? 150;
    };

    return (
        <>
            <CustomTable
                asc={filters.order}
                settedOrder={filters.sort}
                tableHeight='calc(100% - 48px)'
                prevOrderHeads={[]}
                postOrderHeads={[
                    <th className={clsx("text-left", {
                        hidden: (keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) && userState.findMeResponse?.customerAccess === CustomerAccessEnum.NONE,
                    })}>
                        <div className="flex gap-[4px] w-[20px]" />
                    </th>
                ]}
                preferences={preferencesResponse?.data[0]?.targetListPreferences?.map(pref => ({ ...pref, order: targetListColumns.find(col => col.column === pref.column)?.order })) ?? []}
                setPreferences={(items) => {
                    updatePreferencesMutation({
                        data: {
                            owner: preferencesResponse?.data[0]?.owner!,
                            personPreferences: preferencesResponse?.data[0]?.personPreferences!,
                            companyPreferences: preferencesResponse?.data[0]?.companyPreferences!,
                            targetListPreferences: items,
                            targetListCompanyPreferences: preferencesResponse?.data[0]?.targetListCompanyPreferences!,
                            targetListPersonPreferences: preferencesResponse?.data[0]?.targetListPersonPreferences!,
                            toVerifyBinPersonPreferences: preferencesResponse?.data[0]?.toVerifyBinPersonPreferences!,
                            toVerifyBinCompanyPreferences: preferencesResponse?.data[0]?.toVerifyBinCompanyPreferences!,
                        },
                        id: preferencesResponse?.data[0]?.id!,
                    });
                }}
                setOrderFilters={(order) => setFilters({ sort: order as keyof TargetListDTO, order: !filters.order, page: 0 })}
                rows={
                    targetLists?.data.map(d => {
                        const commercials = commercialsList.filter(user => d.commercialIds?.includes(user.user.id)) ?? []
                        const teamLeader = teamLeaders.find(user => user.user.id === d.project.teamLeaderId) ?? null
                        return ({
                            key: d?.id, data: [
                                {
                                    item: <td
                                        onClick={() => {
                                            if (!(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) || (((keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) && userState.findMeResponse?.customerAccess !== CustomerAccessEnum.NONE))) {
                                                navigate("/edit-target-list/" + d.id + '/specifics/' + (isFromProject ? d.project.id : 'targetlist'));
                                            }
                                        }}
                                        className={clsx("text-text-sm crop-text font-semibold text-neutral-600", {
                                            'cursor-pointer': !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) || (((keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) && userState.findMeResponse?.customerAccess !== CustomerAccessEnum.NONE))
                                        })}>
                                        <div
                                            style={{ width: getColumnWidth('Nome') }}
                                            className={clsx("text-text-sm crop-text font-semibold text-neutral-600", {
                                                'cursor-pointer': !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) || (((keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) && userState.findMeResponse?.customerAccess !== CustomerAccessEnum.NONE))
                                            })}
                                        >
                                            {d.name}
                                        </div>
                                    </td>,
                                    group: 'sort',
                                    key: 'Nome'
                                },
                                {
                                    item: <td>
                                        <div
                                            className="text-text-sm crop-text text-neutral-600" style={{ width: getColumnWidth('Progetto') }}
                                        >
                                            {d.project.name ?? ' - '}
                                        </div>
                                    </td>,
                                    group: 'sort',
                                    key: 'Progetto'
                                },
                                {
                                    item: <td className="text-text-sm text-neutral-600 flex items-center gap-2 h-12">
                                        {
                                            d.project.teamLeader === null ?
                                                <div>-</div> :
                                                <div className="text-text-sm text-neutral-600 flex items-center gap-2 h-12">
                                                    <Avatar
                                                        type="single"
                                                        size="sm"
                                                        shape="circle"
                                                        imageUrl={teamLeader?.avatar || ''}
                                                        fullName={teamLeader?.user.name + " " + teamLeader?.user.surname}
                                                        altTextInitials={
                                                            teamLeader?.user.name &&
                                                                teamLeader?.user.surname ?
                                                                (teamLeader?.user.name[0] +
                                                                    teamLeader?.user.surname[0]) : ""
                                                        }
                                                    />
                                                    <span>
                                                        {(teamLeader?.user.name ?? "-") +
                                                            " " +
                                                            (teamLeader?.user.surname ?? "-")}
                                                    </span>
                                                </div>
                                        }
                                    </td>,
                                    group: 'sort',
                                    key: 'Team Leader'
                                },
                                {
                                    item: <td className="text-text-sm text-neutral-600">
                                        <Avatar
                                            type="group"
                                            size="sm"
                                            length={2}
                                            imageUrls={(commercials ?? []).map((commercial) => ({
                                                altTextInitials: commercial.user.name[0] + commercial.user.surname[0],
                                                fullNames: commercial.user.name + commercial.user.surname,
                                                url: commercial.avatar ?? "",
                                            }))}
                                        />
                                    </td>,
                                    group: 'sort',
                                    key: 'Commerciali'
                                },
                                {
                                    item: <td>
                                        <div
                                            style={{ width: getColumnWidth('Settore') }} className="text-text-sm text-neutral-600"
                                        >
                                            {
                                                d.sectors &&
                                                    d.sectors &&
                                                    d.sectors.length > 0 ?
                                                    <Pills
                                                        size={"xs"}
                                                        label={
                                                            d.sectors[0] +
                                                            (d.sectors.length > 1
                                                                ? " +" + (d.sectors.length - 1)
                                                                : "")
                                                        }
                                                        outline={false}
                                                        emphasis={false}
                                                        color={"blue"}
                                                    /> :
                                                    "-"
                                            }
                                        </div>
                                    </td>,
                                    group: 'sort',
                                    key: 'Settore'
                                },
                                {
                                    item: <td className="text-text-sm text-neutral-600">
                                        <div className="flex items-center justify-center">
                                            <Pills
                                                size={"xs"}
                                                label={numericFormatter(d.contacts.toString(), { thousandSeparator: '.' }) ?? "0"}
                                                outline={false}
                                                emphasis={false}
                                                color={"blue"}
                                            />
                                        </div>
                                    </td>,
                                    group: 'sort',
                                    key: 'Contatti'
                                },
                                {
                                    item: <td className="text-text-sm text-neutral-600">
                                        <div className="flex items-center justify-center">
                                            <Pills
                                                size={"xs"}
                                                label={numericFormatter(d.leads.toString(), { thousandSeparator: '.' }) ?? "0"}
                                                outline={false}
                                                emphasis={false}
                                                color={"blue"}
                                            />
                                        </div>
                                    </td>,
                                    group: 'sort',
                                    key: 'Lead'
                                },
                                {
                                    item: <td className="text-text-sm text-neutral-600">
                                        <div className="flex items-center justify-center">
                                            <Pills
                                                size={"xs"}
                                                label={numericFormatter(d.drafts.toString(), { thousandSeparator: '.' }) ?? "0"}
                                                outline={false}
                                                emphasis={false}
                                                color={"blue"}
                                            />
                                        </div>
                                    </td>,
                                    group: 'sort',
                                    key: 'Da lavorare'
                                },
                                {
                                    item: <td className="text-text-sm text-neutral-600">
                                        <Pills
                                            size={"xs"}
                                            label={targertListsStatePillMap.get(d.state)?.label!}
                                            outline={false}
                                            emphasis={false}
                                            color={targertListsStatePillMap.get(d.state)?.color!}
                                        />
                                    </td>,
                                    group: 'sort',
                                    key: 'Stato'
                                },
                                {
                                    item: <td className={clsx("w-[20px] cursor-pointer", { hidden: (keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) && userState.findMeResponse?.customerAccess === CustomerAccessEnum.NONE })}>
                                        <Dropdown
                                            items={[
                                                {
                                                    label: "Modifica",
                                                    icon: <EditIcon color={""} size={0} />,
                                                    onClick: () => navigate("/edit-target-list/" + d.id + '/specifics/' + (isFromProject ? d.project.id : 'targetlist'))
                                                },
                                                {
                                                    label: "Mostra contatti",
                                                    icon: <UsersIcon color={""} size={0} />,
                                                    onClick: () => navigate("/edit-target-list/" + d.id + '/people/' + (isFromProject ? d.project.id : 'targetlist'))
                                                },
                                                (userState.findMeResponse?.isAutonomous || userState.findMeResponse?.isAutonomous === null) ?
                                                    {
                                                        label: "Visualizza Ko",
                                                        icon: <TrashIcon color={""} size={0} />,
                                                        onClick: () => navigate(`/target-list-bins/${d.id}/people`)
                                                    } : null,
                                                ((keycloak.hasRealmRole('customer') || ((keycloak.hasRealmRole('commercial') || keycloak.hasRealmRole('collaborator')) && !userState.findMeResponse?.isAutonomous)) ? null : {
                                                    label: "Copia in un altro progetto",
                                                    icon: <CopyIcon color={""} size={0} />,
                                                    onClick: () => {
                                                        setTargetListToCopy(d.id);
                                                        setShowCopyModal(true)
                                                    }
                                                }),
                                                (keycloak.hasRealmRole('customer') || (keycloak.hasRealmRole('commercial') && !userState.findMeResponse?.isAutonomous) || keycloak.hasRealmRole('collaborator') ? null : {
                                                    label: "Cancella",
                                                    icon: <XCircleIcon color={""} size={0} />,
                                                    onClick: () => { setIdToDelete(d.id); setShowDeleteModal(true) },
                                                }),
                                            ]}
                                        />
                                    </td>,
                                    group: 'postSort',
                                    key: 'menu'
                                }
                            ]
                        })
                    }) ?? []
                }
            />
            <DeleteTargetList
                id={idToDelete}
                setId={setIdToDelete}
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
            />
        </>
    )
}